import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useNavigate } from 'react-router-dom';
import { Search, Upload, Music, ChevronLeft } from 'lucide-react';
import { TabSong } from '../types/index.js';
import { apiClient, ApiError, getTabExtras } from '../utils/apiClient.js';
import { getLocalTabs, saveLocalTab } from '../db/operations.js';
import { writeTabExtras } from '../db/tabEdits.js';
import { useAuthStore } from '../stores/authStore.js';
import { useTopBarStore } from '../stores/topBarStore.js';
import { toPersianDigits } from '../utils/timeUtils.js';

// UI Kit components
import { PageHeader } from '../components/ui/PageHeader.js';
import { Button } from '../components/ui/Button.js';
import { IconButton } from '../components/ui/IconButton.js';
import { Chip } from '../components/ui/Chip.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { Dialog } from '../components/ui/Dialog.js';
import { useToast } from '../components/ui/useToast.js';

// Marketplace subcomponents
import { MarketTabRow } from '../components/marketplace/MarketTabRow.js';
import { RequestRow, TabRequestItem } from '../components/marketplace/RequestRow.js';
import { TabRequestDialog } from '../components/marketplace/TabRequestDialog.js';
import { TabImportDialog } from '../components/tab/TabImportDialog.js';
import { TuningPicker } from '../components/tuning/TuningPicker.js';
import { findTuning, tuningBadge, tuningQueryValues } from '../data/tunings.js';

interface TuningFilter {
  key: string;
  label: string;
  mobileLabel: string;
  /** Canonical catalogue name this chip filters on (undefined = all). */
  tuning?: string;
  testId: string;
}

// Quick chips for the common tunings; every other tuning lives behind "more tunings" (FRE-139)
const TUNING_FILTERS: TuningFilter[] = [
  { key: 'ALL', label: 'همه', mobileLabel: 'همه کوک‌ها', tuning: undefined, testId: 'ALL' },
  { key: 'B Std 7', label: 'B Std 7', mobileLabel: 'B Std 7', tuning: '7-String B Standard', testId: 'B-Std-7' },
  { key: 'Drop A#', label: 'Drop A#', mobileLabel: 'Drop A#', tuning: 'Drop A#', testId: 'Drop-A#' },
  { key: 'Drop C', label: 'Drop C', mobileLabel: 'Drop C', tuning: 'Drop C', testId: 'Drop-C' },
  { key: 'Drop D', label: 'Drop D', mobileLabel: 'Drop D', tuning: 'Drop D', testId: 'Drop-D' },
  { key: 'E Std', label: 'E Std', mobileLabel: 'E Std', tuning: 'E Standard', testId: 'E-Std' }
];

const PAGE_SIZE = 24;

export const MarketplacePage: React.FC = () => {
  usePageTitle('بازار تبلچر');
  const navigate = useNavigate();
  const toast = useToast();
  const { token, isAuthenticated, isGuest } = useAuthStore();
  const setTopBarConfig = useTopBarStore((s) => s.setTopBarConfig);
  const resetTopBarConfig = useTopBarStore((s) => s.resetTopBarConfig);

  // Catalog state
  const [tabs, setTabs] = useState<TabSong[]>([]);
  const [downloadedTabIds, setDownloadedTabIds] = useState<Set<string>>(new Set());
  const [downloadingTabIds, setDownloadingTabIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTuningKey, setSelectedTuningKey] = useState<string>('ALL');
  const [moreTuning, setMoreTuning] = useState<string>('E Standard');
  const [moreOpen, setMoreOpen] = useState(false);
  const [isLoadingTabs, setIsLoadingTabs] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const totalRef = React.useRef(0);
  totalRef.current = total;
  const requestSeqRef = React.useRef(0);

  // Requests state
  const [requests, setRequests] = useState<TabRequestItem[]>([]);
  const [votingRequestIds, setVotingRequestIds] = useState<Set<string>>(new Set());
  const [highlightedRequestId, setHighlightedRequestId] = useState<string | null>(null);

  // Dialog state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isMobileRequestsOpen, setIsMobileRequestsOpen] = useState(false);

  // Viewport tracking for conditional desktop header mounting
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 1024
  );

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Configure mobile top bar
  useEffect(() => {
    setTopBarConfig({
      title: 'بازارچه',
      showLogo: false,
      hideDefaultActions: true,
      actions: (
        <>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsRequestModalOpen(true)}
            data-testid="marketplace-request-button"
            className="h-10 px-3 text-xs md:text-sm font-semibold rounded-xl"
          >
            <span>درخواست</span>
            {!isDesktop && (
              <span className="sr-only"> تبلچر</span>
            )}
          </Button>
          <IconButton
            variant="primary"
            size="md"
            icon={
              <>
                <Upload className="size-5" />
                {!isDesktop && (
                  <span className="sr-only">آپلود فایل .gp5</span>
                )}
              </>
            }
            label="انتشار تبلچر"
            onClick={() => setIsUploadModalOpen(true)}
          />
        </>
      )
    });

    return () => {
      resetTopBarConfig();
    };
  }, [setTopBarConfig, resetTopBarConfig, isDesktop]);

  // Load requests
  const loadRequests = useCallback(async () => {
    try {
      const res = await apiClient.get<TabRequestItem[]>('/api/tabs/requests');
      if (Array.isArray(res)) {
        setRequests(res);
      }
    } catch {
      // Offline fallback: keep current requests if already present
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // Selected filter definition: a quick chip, or any tuning picked in the "more tunings" dialog
  const currentFilter = useMemo(() => {
    if (selectedTuningKey === 'MORE') {
      const t = findTuning(moreTuning);
      return { key: 'MORE', tuning: t?.name ?? moreTuning, queryParam: t ? tuningQueryValues(t).join(',') : moreTuning };
    }
    const chip = TUNING_FILTERS.find((f) => f.key === selectedTuningKey) || TUNING_FILTERS[0];
    const t = findTuning(chip.tuning);
    return { key: chip.key, tuning: chip.tuning, queryParam: t ? tuningQueryValues(t).join(',') : undefined };
  }, [selectedTuningKey, moreTuning]);

  // Load marketplace tabs & check local Dexie
  const loadTabs = useCallback(async (pageNum = 1, append = false) => {
    const seq = ++requestSeqRef.current;
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoadingTabs(true);
      setLoadError(false);
    }
    try {
      // 1. Get locally downloaded tab IDs from Dexie
      const local = await getLocalTabs();
      const localIds = new Set(local.map((t) => t.id));
      setDownloadedTabIds(localIds);

      // 2. Fetch from API if online
      if (navigator.onLine) {
        try {
          const res = await apiClient.get<{
            data: TabSong[];
            pagination?: { page: number; limit: number; total: number; totalPages: number };
          }>('/api/tabs', {
            params: {
              page: pageNum,
              limit: PAGE_SIZE,
              q: searchQuery.trim() || undefined,
              tuning: currentFilter.queryParam,
              status: 'APPROVED'
            }
          });

          if (seq !== requestSeqRef.current) return;

          if (res?.data && Array.isArray(res.data)) {
            const newItems = res.data;
            const newTotal = res.pagination?.total ?? (append ? totalRef.current : newItems.length);
            const newTotalPages = res.pagination?.totalPages ?? Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
            setPage(pageNum);
            setTotal(newTotal);
            setTotalPages(newTotalPages);

            if (append) {
              setTabs((prev) => {
                const existingIds = new Set(prev.map((t) => t.id));
                const filteredNew = newItems.filter((t) => !existingIds.has(t.id));
                return [...prev, ...filteredNew];
              });
            } else {
              setTabs(newItems);
            }
            setIsLoadingTabs(false);
            setIsLoadingMore(false);
            return;
          }
          // Malformed response: treat as a load failure
          setLoadError(true);
        } catch {
          if (seq !== requestSeqRef.current) return;
          // Remote API failed: show an error notice and fall back to local cache
          setLoadError(true);
        }
      }

      if (seq !== requestSeqRef.current) return;

      // 3. Fallback: filter local tabs
      let filtered = local;
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        filtered = filtered.filter(
          (t) =>
            t.title.toLowerCase().includes(q) ||
            t.artist.toLowerCase().includes(q)
        );
      }

      if (currentFilter.key !== 'ALL') {
        const wanted = findTuning(currentFilter.tuning);
        filtered = filtered.filter((t) => {
          const have = findTuning(t.tuning);
          return wanted && have ? have.id === wanted.id : (t.tuning || '').toLowerCase() === (currentFilter.tuning || '').toLowerCase();
        });
      }

      setTabs(filtered);
      setTotal(filtered.length);
      setTotalPages(1);
      setPage(1);
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      console.error('Failed to load marketplace tabs:', err);
      setLoadError(true);
    } finally {
      if (seq === requestSeqRef.current) {
        setIsLoadingTabs(false);
        setIsLoadingMore(false);
      }
    }
  }, [searchQuery, currentFilter]);

  useEffect(() => {
    loadTabs(1, false);
  }, [loadTabs]);

  // Handle Download / Import into Dexie
  const handleDownloadTab = async (tab: TabSong) => {
    setDownloadingTabIds((prev) => new Set(prev).add(tab.id));
    try {
      let buffer: ArrayBuffer | undefined = tab.localBlob;

      if (!buffer && tab.fileUrl) {
        buffer = await apiClient.downloadBlob(tab.fileUrl);
      }

      await saveLocalTab(tab, buffer);
      try {
        const extras = await getTabExtras(tab.id);
        if (extras) {
          await writeTabExtras(tab.id, extras);
        }
      } catch (extrasErr) {
        console.warn('Failed to fetch tab extras after download:', extrasErr);
      }
      setDownloadedTabIds((prev) => new Set(prev).add(tab.id));
      toast.success(`تبلچر "${tab.title}" با موفقیت دانلود شده و به کتابخانه افزوده شد.`);
    } catch (err) {
      console.error('Failed to import tab:', err);
      toast.error('خطا در دریافت تبلچر.');
    } finally {
      setDownloadingTabIds((prev) => {
        const next = new Set(prev);
        next.delete(tab.id);
        return next;
      });
    }
  };

  // Handle Open in tab viewer
  const handleOpenTab = (tabId: string) => {
    navigate(`/tabs/${tabId}`);
  };

  const handleLoadMore = () => {
    if (page < totalPages && !isLoadingMore) {
      void loadTabs(page + 1, true);
    }
  };

  const handleRequestSubmitted = async (res?: { duplicate?: boolean; request?: TabRequestItem }) => {
    if (res?.duplicate) {
      toast.info('این تبلچر قبلاً درخواست شده بود؛ رأی شما به همان درخواست اضافه شد.');
    } else {
      toast.success('درخواست تبلچر ثبت شد.');
    }
    await loadRequests();
    const id = res?.request?.id;
    if (id) {
      setHighlightedRequestId(id);
      setTimeout(() => {
        const el = document.getElementById(`request-row-${id}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
      setTimeout(() => {
        setHighlightedRequestId((curr) => (curr === id ? null : curr));
      }, 2000);
    }
  };

  // Handle Vote on open request (B2 endpoint)
  const handleVoteRequest = async (requestId: string) => {
    if (isGuest || !isAuthenticated || !token) {
      toast.info('برای ثبت رأی وارد شوید');
      return;
    }

    setVotingRequestIds((prev) => new Set(prev).add(requestId));
    try {
      const res = await apiClient.post<{ requestId: string; voteCount: number; hasVoted: boolean }>(
        `/api/tabs/requests/${requestId}/vote`
      );

      if (res) {
        setRequests((prev) =>
          prev.map((r) =>
            r.id === res.requestId
              ? { ...r, voteCount: res.voteCount, hasVoted: res.hasVoted }
              : r
          )
        );
      }
    } catch (err) {
      // FND-11: an unverified email already opened the verify dialog (apiClient); no extra toast.
      if (err instanceof ApiError && err.status === 403 && err.data?.code === 'EMAIL_NOT_VERIFIED') return;
      console.error('Failed to vote on tab request:', err);
      toast.error('خطا در ثبت رأی.');
    } finally {
      setVotingRequestIds((prev) => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
    }
  };

  return (
    <div className="flex flex-col gap-4 md:gap-5 max-w-[1120px] mx-auto pb-14">
      {/* 1. Page Header (Desktop) */}
      {isDesktop && (
        <div className="hidden lg:block">
          <PageHeader
            title="بازارچه"
            subtitle="تبلچرهای تأییدشدهٔ جامعه؛ با یک کلیک به کتابخانهٔ آفلاین اضافه می‌شوند."
            actions={
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setIsRequestModalOpen(true)}
                  data-testid="marketplace-request-button"
                >
                  درخواست تبلچر
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  leadingIcon={<Upload className="size-4.5" />}
                  onClick={() => setIsUploadModalOpen(true)}
                >
                  <span>انتشار تبلچر</span>
                  <span className="inline text-[0px] leading-none text-transparent select-none">آپلود فایل .gp5</span>
                </Button>
              </div>
            }
          />
        </div>
      )}

      {/* 2. Filter & Search Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        {/* Search Input */}
        <div className="md:flex-1 shrink-0 min-w-0 md:min-w-[320px] h-11 flex items-center gap-2 border border-base-300 rounded-xl bg-base-200 px-3.5 focus-within:border-primary transition-colors">
          <Search className="size-4.5 text-base-content/50 shrink-0" />
          <label htmlFor="marketplace-search-input" className="sr-only">
            جستجو در بازارچه
          </label>
          <input
            id="marketplace-search-input"
            data-testid="marketplace-search-input"
            type="text"
            placeholder="آهنگ، آلبوم یا هنرمند"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 min-w-0 bg-transparent text-sm text-base-content placeholder:text-base-content/50 outline-none"
          />
        </div>

        {/* Tuning Filter Chips: RTL row so it starts at the "همه" chip on the right; labels stay LTR inside each chip */}
        <div
          dir="rtl"
          className="flex items-center gap-2 md:gap-1.5 overflow-x-auto no-scrollbar md:justify-start scroll-smooth pe-6 md:pe-0 [mask-image:linear-gradient(to_left,black_85%,transparent)] md:[mask-image:none]"
        >
          {TUNING_FILTERS.map((t) => {
            const isSelected = selectedTuningKey === t.key;
            return (
              <Chip
                key={t.key}
                mono={t.key !== 'ALL'}
                size="md"
                selected={isSelected}
                onClick={() => setSelectedTuningKey(t.key)}
                className="h-8 px-3 rounded-full text-xs shrink-0 select-none cursor-pointer"
                data-testid={`marketplace-tuning-chip-${t.testId}`}
              >
                <span className="md:inline hidden">{t.label}</span>
                <span className="md:hidden inline">{t.mobileLabel}</span>
                {t.key === 'E Std' && (
                  <span className="inline text-[0px] leading-none text-transparent select-none">E Standard</span>
                )}
              </Chip>
            );
          })}
          <Chip
            mono
            size="md"
            selected={selectedTuningKey === 'MORE'}
            onClick={() => setMoreOpen(true)}
            className="h-8 px-3 rounded-full text-xs shrink-0 select-none cursor-pointer"
            data-testid="marketplace-tuning-chip-more"
          >
            {selectedTuningKey === 'MORE' ? tuningBadge(moreTuning) : 'کوک‌های بیشتر…'}
          </Chip>
        </div>
      </div>

      {/* 3. Main Content: Two-Column Split on Desktop */}
      <div className="flex flex-col lg:flex-row items-start gap-4 md:gap-5">
        {/* Left Column: Catalog Results Card */}
        <section className="flex-1 w-full min-w-0 bg-base-200 border border-base-300 rounded-2xl overflow-hidden">
          {isLoadingTabs && tabs.length === 0 ? (
            <div className="p-16 text-center">
              <span className="loading loading-spinner loading-lg text-primary"></span>
              <p className="text-xs text-base-content/60 mt-3 font-medium">در حال دریافت تبلچرهای بازارچه...</p>
            </div>
          ) : tabs.length === 0 && loadError ? (
            <div role="alert" data-testid="marketplace-load-error" className="p-12 text-center flex flex-col items-center gap-3">
              <p className="text-sm font-semibold text-error">خطا در دریافت فهرست تبلچرها</p>
              <p className="text-xs text-base-content/60">ارتباط با سرور برقرار نشد. لطفاً دوباره تلاش کنید.</p>
              <Button variant="secondary" size="sm" onClick={() => loadTabs()} data-testid="marketplace-retry-button">
                تلاش مجدد
              </Button>
            </div>
          ) : tabs.length === 0 ? (
            <EmptyState
              icon={<Music className="size-6" />}
              title="تبلچری با این مشخصات یافت نشد"
              description="می‌توانید تبلچر مورد نظر خود را آپلود کنید یا درخواست نگارش آن را ثبت نمایید."
              className="border-0 rounded-none p-12"
              action={
                <Button
                  variant="primary"
                  size="sm"
                  leadingIcon={<Upload className="size-4" />}
                  onClick={() => setIsUploadModalOpen(true)}
                >
                  انتشار تبلچر جدید
                </Button>
              }
            />
          ) : (
            <div>
              <div className="divide-y divide-base-300">
                {loadError && (
                  <div role="alert" data-testid="marketplace-load-error" className="flex items-center justify-between gap-3 px-4 py-3 bg-error/10 text-xs">
                    <span className="text-error font-medium">خطا در دریافت فهرست از سرور؛ نتایج ذخیره‌شده نمایش داده می‌شود.</span>
                    <Button variant="secondary" size="sm" onClick={() => loadTabs(1, false)} data-testid="marketplace-retry-button">
                      تلاش مجدد
                    </Button>
                  </div>
                )}
                {tabs.map((tab) => (
                  <MarketTabRow
                    key={tab.id}
                    tab={tab}
                    isDownloaded={downloadedTabIds.has(tab.id)}
                    isDownloading={downloadingTabIds.has(tab.id)}
                    onDownload={handleDownloadTab}
                    onOpen={handleOpenTab}
                  />
                ))}
              </div>

              {/* Paging Footer: Count & Load more */}
              <div className="p-4 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-base-300">
                <span data-testid="marketplace-count" className="text-xs text-base-content/60">
                  نمایش {toPersianDigits(tabs.length)} از {toPersianDigits(total)}
                </span>
                {page < totalPages && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={isLoadingMore}
                    onClick={handleLoadMore}
                    data-testid="marketplace-load-more"
                  >
                    نمایش بیشتر
                  </Button>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Right Column: «درخواست‌های باز» Sidebar Card (Desktop only) */}
        <aside className="hidden lg:block w-full lg:w-[281px] shrink-0 bg-base-200 border border-base-300 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4.5 py-4 border-b border-base-300">
            <h2 className="text-[15px] font-semibold text-base-content m-0">درخواست‌های باز</h2>
            <span className="text-xs text-base-content/60 font-medium">
              {toPersianDigits(requests.length)}
            </span>
          </div>

          {requests.length === 0 ? (
            <div className="p-8 text-center text-xs text-base-content/50">
              درخواستی در حال حاضر ثبت نشده است
            </div>
          ) : (
            <div>
              {requests.slice(0, 6).map((req, idx) => (
                <RequestRow
                  key={req.id}
                  request={req}
                  isVoting={votingRequestIds.has(req.id)}
                  onVote={handleVoteRequest}
                  isHighlighted={highlightedRequestId === req.id}
                  isLast={idx === Math.min(requests.length, 6) - 1}
                />
              ))}
            </div>
          )}
        </aside>
      </div>

      {/* 4. Mobile Open Requests Summary Card */}
      <section className="lg:hidden w-full">
        <button
          type="button"
          onClick={() => setIsMobileRequestsOpen(true)}
          className="w-full text-start bg-neutral border border-base-300 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors hover:bg-neutral/80 active:scale-[0.99]"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-base-content m-0">
              {toPersianDigits(requests.length)} درخواست باز
            </p>
            <p className="text-xs text-base-content/60 mt-0.5 m-0">
              به درخواست‌های دیگران رأی دهید
            </p>
          </div>
          <ChevronLeft className="size-5 text-base-content/60 shrink-0" />
        </button>
      </section>

      {/* Mobile Requests Sheet / Dialog */}
      <Dialog
        open={isMobileRequestsOpen}
        onClose={() => setIsMobileRequestsOpen(false)}
        title="درخواست‌های باز"
        titleHeadingLevel="h3"
        description="به تبلچرهای درخواستی دیگران رأی دهید تا سریع‌تر اضافه شوند."
        size="md"
      >
        <div className="flex flex-col -mx-4 -mb-2 divide-y divide-base-300 max-h-[60vh] overflow-y-auto">
          {requests.length === 0 ? (
            <div className="p-8 text-center text-xs text-base-content/50">
              درخواستی در حال حاضر ثبت نشده است
            </div>
          ) : (
            requests.map((req, idx) => (
              <RequestRow
                key={req.id}
                request={req}
                isVoting={votingRequestIds.has(req.id)}
                onVote={handleVoteRequest}
                isHighlighted={highlightedRequestId === req.id}
                isLast={idx === requests.length - 1}
              />
            ))
          )}
        </div>
      </Dialog>

      {/* Tab Request Dialog */}
      <Dialog
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        title="فیلتر بر اساس کوک"
        titleHeadingLevel="h3"
        description="نوع گیتار را انتخاب کنید و کوک مورد نظر را از فهرست بردارید."
        size="sm"
        data-testid="marketplace-tuning-dialog"
        footer={
          <>
            <Button
              variant="primary"
              onClick={() => {
                setSelectedTuningKey('MORE');
                setMoreOpen(false);
              }}
              data-testid="marketplace-tuning-apply"
            >
              اعمال فیلتر
            </Button>
            <Button variant="ghost" onClick={() => setMoreOpen(false)}>
              انصراف
            </Button>
          </>
        }
      >
        <TuningPicker value={moreTuning} onChange={setMoreTuning} testIdPrefix="marketplace-more-tuning" allowCreate={false} />
      </Dialog>

      <TabRequestDialog
        open={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        onRequestSubmitted={handleRequestSubmitted}
      />

      {/* Tab Import Dialog */}
      <TabImportDialog
        open={isUploadModalOpen}
        defaultPublish={true}
        onClose={() => setIsUploadModalOpen(false)}
        onUploaded={(newTab) => {
          setDownloadedTabIds((prev) => new Set(prev).add(newTab.id));
          // The marketplace lists approved tabs only; a new upload is PENDING (waiting for an admin) or PRIVATE.
          if (newTab.status === 'APPROVED') {
            setTabs((prev) => [newTab, ...prev]);
          }
          // For PENDING the import dialog already shows the "awaiting approval" notice.
          if (newTab.status === 'PRIVATE') {
            toast.success(`تبلچر "${newTab.title}" در کتابخانهٔ شما ذخیره شد.`);
          }
        }}
      />
    </div>
  );
};
